import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { catchError, exhaustMap, filter, mergeMap, share, switchMap, withLatestFrom } from 'rxjs';
import { errMessage } from '@ui';
import { HttpRequestActions } from '../state/http-request/http-request.actions';
import { httpRequestFeature } from '../state/http-request/http-request.feature';
import { PanicHttpRequestService } from '../../services/http-request.service';
import { SharedActions } from '../state/shared/shared.actions';

export const startLoadingHttpRequests$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(HttpRequestActions.startLoadingHttpRequests),
      withLatestFrom(
        store.select(httpRequestFeature.selectListLoaded),
        store.select(httpRequestFeature.selectIsLoading),
      ),
      filter(([, listLoaded, isLoading]) => !listLoaded && !isLoading),
      exhaustMap(() => [
        HttpRequestActions.setIsLoading({ isLoading: true }),
        HttpRequestActions.loadHttpRequests(),
      ]),
    ),
  { functional: true },
);

export const loadHttpRequests$ = createEffect(
  (actions$ = inject(Actions), service = inject(PanicHttpRequestService)) =>
    actions$.pipe(
      ofType(HttpRequestActions.loadHttpRequests),
      switchMap(() =>
        service.getAll().pipe(
          switchMap((httpRequests) => [
            HttpRequestActions.loadHttpRequestsSuccess({ httpRequests }),
            HttpRequestActions.setIsLoading({ isLoading: false }),
          ]),
          catchError((response) => [
            HttpRequestActions.loadHttpRequestsFail(),
            HttpRequestActions.setIsLoading({ isLoading: false }),
            SharedActions.showMessage({ error: errMessage(response) }),
          ]),
        ),
      ),
    ),
  { functional: true },
);

export const startCreateHttpRequest$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(HttpRequestActions.startCreateHttpRequest),
      exhaustMap(({ create }) => [
        HttpRequestActions.setIsSaving({ isSaving: true }),
        HttpRequestActions.createHttpRequest({ create }),
      ]),
      share(),
    ),
  { functional: true },
);

export const createHttpRequest$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(PanicHttpRequestService),
    translate = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(HttpRequestActions.createHttpRequest),
      mergeMap(({ create }) =>
        service.create(create).pipe(
          switchMap((httpRequest) => [
            HttpRequestActions.createHttpRequestSuccess({ httpRequest }),
            HttpRequestActions.setIsSaving({ isSaving: false }),
            SharedActions.showMessage({
              success: translate.instant('TOASTS.HTTP_REQUEST_CREATED'),
            }),
          ]),
          catchError((response) => [
            SharedActions.showMessage({ error: errMessage(response) }),
            HttpRequestActions.setIsSaving({ isSaving: false }),
            HttpRequestActions.createHttpRequestFail(),
          ]),
        ),
      ),
      share(),
    ),
  { functional: true },
);

export const startUpdateHttpRequest$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(HttpRequestActions.startUpdateHttpRequest),
      exhaustMap(({ id, update }) => [
        HttpRequestActions.addToProcessingID({ id }),
        HttpRequestActions.setIsSaving({ isSaving: true }),
        HttpRequestActions.updateHttpRequest({ id, update }),
      ]),
      share(),
    ),
  { functional: true },
);

export const updateHttpRequest$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(PanicHttpRequestService),
    translate = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(HttpRequestActions.updateHttpRequest),
      mergeMap(({ id, update }) =>
        service.update(id, update).pipe(
          switchMap(() => [
            HttpRequestActions.updateHttpRequestSuccess({
              httpRequest: { _id: id, ...update } as any,
            }),
            HttpRequestActions.removeFromProcessingID({ id }),
            HttpRequestActions.setIsSaving({ isSaving: false }),
            SharedActions.showMessage({
              success: translate.instant('TOASTS.HTTP_REQUEST_UPDATED'),
            }),
          ]),
          catchError((response) => [
            SharedActions.showMessage({ error: errMessage(response) }),
            HttpRequestActions.removeFromProcessingID({ id }),
            HttpRequestActions.setIsSaving({ isSaving: false }),
            HttpRequestActions.updateHttpRequestFail(),
          ]),
        ),
      ),
      share(),
    ),
  { functional: true },
);

export const startDeleteHttpRequest$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(HttpRequestActions.startDeleteHttpRequest),
      exhaustMap(({ id }) => [
        HttpRequestActions.addToProcessingID({ id }),
        HttpRequestActions.deleteHttpRequest({ id }),
      ]),
      share(),
    ),
  { functional: true },
);

export const deleteHttpRequest$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(PanicHttpRequestService),
    translate = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(HttpRequestActions.deleteHttpRequest),
      mergeMap(({ id }) =>
        service.delete(id).pipe(
          switchMap(() => [
            HttpRequestActions.deleteHttpRequestSuccess({ id }),
            HttpRequestActions.removeFromProcessingID({ id }),
            SharedActions.showMessage({
              success: translate.instant('TOASTS.HTTP_REQUEST_DELETED'),
            }),
          ]),
          catchError((response) => [
            SharedActions.showMessage({ error: errMessage(response) }),
            HttpRequestActions.removeFromProcessingID({ id }),
            HttpRequestActions.deleteHttpRequestFail(),
          ]),
        ),
      ),
      share(),
    ),
  { functional: true },
);

export const startSetEnabled$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(HttpRequestActions.startSetEnabled),
      exhaustMap(({ id, enabled }) => [
        HttpRequestActions.addToProcessingID({ id }),
        HttpRequestActions.setEnabled({ id, enabled }),
      ]),
      share(),
    ),
  { functional: true },
);

export const setEnabled$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(PanicHttpRequestService),
  ) =>
    actions$.pipe(
      ofType(HttpRequestActions.setEnabled),
      mergeMap(({ id, enabled }) =>
        service.setEnabled(id, enabled).pipe(
          switchMap(() => [
            HttpRequestActions.setEnabledSuccess({ id, enabled }),
            HttpRequestActions.removeFromProcessingID({ id }),
          ]),
          catchError((response) => [
            SharedActions.showMessage({ error: errMessage(response) }),
            HttpRequestActions.removeFromProcessingID({ id }),
            HttpRequestActions.setEnabledFail(),
          ]),
        ),
      ),
      share(),
    ),
  { functional: true },
);
